// Constants
const VISITOR_TOKEN_KEY = 'wr-visitor-id';
const SESSION_TOKEN_KEY = 'wr-session-token';
const GUEST_CART_KEY = 'wr-guest-cart';

/**
 * Manages application state and authentication
 */
class ShopifyStateBridge {
    constructor() {
        console.log('Initializing ShopifyStateBridge...');
        this.state = {
            cart: null,
            user: null,
            session: null,
            visitorToken: null,
            sessionToken: null,  // Initialize as null, will be updated from API response
            shopifyCustomer: null,
            guestCart: null  // New property for guest cart
        };
        
        this.setupEventListeners();
        this.initializeState();
        
        // Add flag to track initialization
        this.initialized = false;
    }

    /**
     * Set up event listeners for Shopify customer events
     */
    setupEventListeners() {
        console.log('Setting up Shopify event listeners...');
        // Bind the methods to maintain correct 'this' context
        this.handleCustomerSignIn = this.handleCustomerSignIn.bind(this);
        this.handleCustomerSignOut = this.handleCustomerSignOut.bind(this);
        
        document.addEventListener('shopify:customer:signin', this.handleCustomerSignIn);
        document.addEventListener('shopify:customer:signout', this.handleCustomerSignOut);
    }

    /**
     * Initialize application state
     */
    async initializeState() {
        console.log('Initializing application state...');
        await this.fetchShopifyCustomer();
        
        // If user is already logged in and we have guest cart, send it
        if (this.isAuthenticated() && this.hasGuestCart()) {
            console.log('User already logged in and has guest cart, sending to session...');
            await this.sendGuestCartToSession();
        }
        
        this.initialized = true;
        console.log('Current state:', this.state);
    }

    /**
     * Handle customer sign in
     */
    async handleCustomerSignIn(event) {
        // console.log('Customer sign in event:', event);
        await this.fetchShopifyCustomer();
        console.log('Checking for guest cart before merge...');
        const guestCart = localStorage.getItem(GUEST_CART_KEY);
        console.log('Guest cart found:', guestCart);
        
        if (this.hasGuestCart()) {
            console.log('Guest cart exists, attempting to merge...');
            await this.sendGuestCartToSession();
        }
        console.log('Updated state after sign in:', this.state);
    }

    /**
     * Handle customer sign out
     */
    async handleCustomerSignOut() {
        console.log('Customer sign out event');
        this.state.shopifyCustomer = null;
        this.state.sessionToken = null;  // Clear token from state
        console.log('Updated state after sign out:', this.state);
    }

    /**
     * Set session token in state
     */
    setSessionToken(token) {
        console.log('Setting session token:', token);
        if (typeof token === 'object' && (token.access || token.refresh)) {
            // If token is an object containing access and refresh tokens
            this.state.sessionToken = {
                access: token.access,
                refresh: token.refresh
            };
        } else {
            // If token is a simple string
            this.state.sessionToken = token;
        }
        console.log('Session token saved to state:', this.state.sessionToken);
    }

    /**
     * Clear session token from state
     */
    clearSessionToken() {
        this.state.sessionToken = null;
        console.log('Session token cleared from state');
    }

    /**
     * Validate existing session token
     */
    async validateSession() {
        console.log('Attempting session validation...');
        if (this.state.shopifyCustomer) {  // Only check if user is logged in
            try {
                const response = await fetch('https://wonderent.wonderchef.com/api/session/validate/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include'
                });

                if (!response.ok) {
                    console.log('Session validation failed');
                    return false;
                }

                const responseData = await response.json();
                console.log('Session validation response:', responseData);
                
                // Update token if provided in response
                if (responseData.token) {
                    this.setSessionToken(responseData.token);
                }
                
                return true;

            } catch (error) {
                //console.error('Error validating session:', error);
                return false;
            }
        }
        console.log('No user logged in, skipping validation');
        return false;
    }

    /**
     * Get token from cookie
     */
    getTokenFromCookie() {
        console.log('All cookies:', document.cookie);
        const token = document.cookie.split('; ').find(row => row.startsWith('auth_token='));
        console.log('Found auth_token cookie:', token);
        if (token) {
            const tokenValue = token.split('=')[1];
            console.log('Raw token value:', tokenValue);
            try {
                const parsedToken = JSON.parse(decodeURIComponent(tokenValue));
            console.log('Parsed token:', parsedToken);
                return parsedToken;
            } catch (e) {
                //console.error('Error parsing token from cookie:', e);
                // If JSON parse fails, try returning the raw token
                return tokenValue;
            }
        }
        return null;
    }

    /**
     * Fetch current Shopify customer
     */
    async fetchShopifyCustomer() {
        console.log('Attempting to identify Shopify customer via meta tags...');

        // Primary and most reliable method: Read customer data from meta tags
        // populated by theme.liquid.
        const customerIdTag = document.querySelector('meta[name="shopify-customer-id"]');
        const customerEmailTag = document.querySelector('meta[name="shopify-customer-email"]');

        if (customerIdTag && customerEmailTag && customerIdTag.content && customerEmailTag.content) {
            const customerId = customerIdTag.content;
            const customerEmail = customerEmailTag.content;
            console.log(`Found Shopify customer via meta tags: {id: ${customerId}, email: ${customerEmail}}`);
            
            this.state.shopifyCustomer = {
                email: customerEmail,
                id: customerId,
                isLoggedIn: true
            };
            await this.syncBackendSession();
            return;
        }

        console.log('Customer meta tags not found. Assuming guest.');
        this.state.shopifyCustomer = null;
        this.state.session = null;
    }

    /**
     * New method to sync session with the backend after finding a Shopify customer
     */
    async syncBackendSession() {
        if (!this.state.shopifyCustomer) return;

        const { email, id } = this.state.shopifyCustomer;

        // Always try to validate first since cookie is sent automatically
        try {
            console.log('Attempting to validate existing session...');
            const response = await fetch('https://wonderent.wonderchef.com/api/session/validate/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                mode: 'cors'
            });

            if (response.ok) {
                const responseData = await response.json();
                console.log('Session validation successful:', responseData);
                
                // Update session state with validated user info
                this.state.session = {
                    userId: responseData.user_id,
                    email: responseData.email,
                    validated: true
                };
                
                return;
            }
                console.log('Session validation failed, will create new session');
        } catch (error) {
                //console.error('Error validating session:', error);
        }

        // Only reach here if validation failed or errored
        console.log('Requesting new session...');
        try {
            // Check for guest cart before creating session
            const guestCart = this.hasGuestCart() ? JSON.parse(localStorage.getItem(GUEST_CART_KEY)) : null;
            console.log('Guest cart found before session creation:', guestCart);

            const response = await fetch('https://wonderent.wonderchef.com/api/session/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: email,
                    id: id,
                    guest_cart: guestCart?.items || [] // Format is consistent
                }),
                credentials: 'include',
                mode: 'cors'
            });
            
            if (!response.ok) {
                throw new Error('Failed to sync session with backend');
            }

            const responseData = await response.json();
            console.log('New session created successfully');
            
            // Update session state with new session info
            this.state.session = {
                userId: responseData.user_id,
                email: responseData.email,
                validated: true
            };

            // If guest cart was sent and session created successfully, clear guest cart
            if (guestCart?.items?.length > 0) {
                console.log('Clearing guest cart after successful session creation');
                localStorage.removeItem(GUEST_CART_KEY);
                this.state.guestCart = null;
                
                // Trigger cart update event
                window.dispatchEvent(new CustomEvent('cartUpdated', {
                    detail: { cart: responseData }
                }));
            }

        } catch (error) {
            //console.error('Error creating new session:', error);
            // If session creation fails, keep guest cart
            console.log('Session creation failed, keeping guest cart');
        }
    }


    /**
     * Get current state
     */
    getState() {
        return this.state;
    }

    /**
     * Update state
     */
    setState(newState) {
        console.log('Updating state:', newState);
        this.state = { ...this.state, ...newState };
        console.log('New state:', this.state);
    }

    /**
     * Handle guest cart
     */
    async handleGuestCart() {
        try {
            const guestCart = localStorage.getItem(GUEST_CART_KEY);
            if (!guestCart) {
                console.log('No guest cart found');
                return;
            }

            const guestCartData = JSON.parse(guestCart);
            console.log('Found guest cart:', guestCartData);

            // Only process if there are items
            if (guestCartData?.items?.length > 0) {
                await this.sendGuestCartToSession(guestCartData);
            }
        } catch (error) {
            //console.error('Error handling guest cart:', error);
        }
    }

    /**
     * Send guest cart to session
     */
    async sendGuestCartToSession() {
        try {
            if (!this.isAuthenticated()) {
                console.log('User not logged in, skipping cart sync');
                return;
            }

            const guestCart = JSON.parse(localStorage.getItem(GUEST_CART_KEY));
            if (!guestCart?.items?.length) {
                console.log('No guest cart items to sync');
                return;
            }

            console.log('Syncing cart with session:', guestCart);

            const response = await fetch('https://wonderent.wonderchef.com/api/session/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    email: this.state.shopifyCustomer.email,
                    id: this.state.shopifyCustomer.id,
                    guest_cart: guestCart?.items || []
                }),
                credentials: 'include',
                mode: 'cors'
            });

            const responseData = await response.json();
            console.log('Session API response:', responseData);

            if (response.ok) {
                console.log('Cart synced successfully');
                localStorage.removeItem(GUEST_CART_KEY);
                this.state.guestCart = null;
                
                window.dispatchEvent(new CustomEvent('cartUpdated', {
                    detail: { cart: responseData }
                }));
            } else {
                throw new Error(`Failed to sync cart: ${responseData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.log('Error syncing cart with session:', error);
            setTimeout(() => this.sendGuestCartToSession(), 5000);
        }
    }

    /**
     * Check guest cart existence
     */
    hasGuestCart() {
        const guestCart = localStorage.getItem(GUEST_CART_KEY);
        return !!guestCart && JSON.parse(guestCart)?.items?.length > 0;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        return !!this.state.shopifyCustomer;
    }

    /**
     * Add method to check for pending cart merge
     */
    async checkPendingCartMerge() {
        if (localStorage.getItem('pendingCartMerge') && this.hasGuestCart()) {
            console.log('Found pending cart merge, attempting again...');
            await this.sendGuestCartToSession();
            localStorage.removeItem('pendingCartMerge');
        }
    }

    // Add method to sync cart at any time
    async syncCart() {
        if (this.isAuthenticated() && this.hasGuestCart()) {
            await this.sendGuestCartToSession();
        }
    }
}

/**
 * Session handler for managing tokens and authentication
 */
class SessionHandler {
    constructor(stateBridge) {
        console.log('Initializing SessionHandler...');
        this.bridge = stateBridge;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        return this.bridge.isAuthenticated();
    }

    /**
     * Get current user info
     */
    getCurrentUser() {
        const user = this.bridge.state.shopifyCustomer;
        console.log('Current user:', user);
        return user;
    }
}

// Create a global namespace for our utilities
window.WrAuth = {
    createAuthUtils: function() {
        console.log('Creating auth utilities...');
        const stateBridge = new ShopifyStateBridge();
        const sessionHandler = new SessionHandler(stateBridge);
        return {
            stateBridge,
            sessionHandler
        };
    }
};

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    console.log('Initializing WrAuth...');
    setTimeout(() => {
        console.log('Delayed initialization...');
        window.wrAuthInstance = window.WrAuth.createAuthUtils();
        
        // Add periodic cart sync every 5 minutes
        setInterval(() => {
            if (window.wrAuthInstance?.stateBridge.initialized) {
                window.wrAuthInstance.stateBridge.syncCart();
            }
        }, 300000); // 5 minutes
    }, 1000);
});